import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CalendarClock, Loader2, Plus, Send, Trash2, Pencil } from 'lucide-react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const CADENCES = [
  { id: 'weekly', label: 'Every week' },
  { id: 'fortnightly', label: 'Every other week' },
  { id: 'monthly', label: 'Monthly' },
];
const WEEKS = [{ id: 1, label: '1st' }, { id: 2, label: '2nd' }, { id: 3, label: '3rd' }, { id: 4, label: '4th' }, { id: -1, label: 'Last' }];
const DAYS_BEFORE = [
  { id: 0, label: 'Same day' },
  { id: 1, label: '1 day' },
  { id: 2, label: '2 days' },
  { id: 3, label: '3 days' },
  { id: 7, label: '1 week' },
];

const pad = (n) => String(n).padStart(2, '0');
/** "07:30" minus one hour → { days_before, time } (rolls back a day if needed). */
function oneHourBefore(startTime) {
  const [h, m] = (startTime || '07:30').split(':').map((x) => parseInt(x, 10) || 0);
  const total = h * 60 + m - 60;
  if (total < 0) return { days_before: 1, time: `${pad(Math.floor((total + 1440) / 60))}:${pad((total + 1440) % 60)}` };
  return { days_before: 0, time: `${pad(Math.floor(total / 60))}:${pad(total % 60)}` };
}

/** Fresh schedule draft — explicit RSVP window: opens the day before at 8 PM, closes 1 hr before start. */
function makeBlank() {
  const close = oneHourBefore('07:30');
  return {
    name: '', days_of_week: [], start_time: '07:30', num_courts: 1, session_fee: 0,
    cadence: 'weekly', week_of_month: 1, repeat_until: '',
    rsvp_open_hours_before: 12, rsvp_close_hours_before: 1,
    rsvp_mode: 'exact',
    rsvp_open_days_before: 1, rsvp_open_time: '20:00',
    rsvp_close_days_before: close.days_before, rsvp_close_time: close.time,
  };
}

/** Rules card for the Sessions tab: schedules + fees + fines, with editing and WhatsApp share. */
export default function SessionRulesCard({ groupId, isAdmin, onBulkAdd }) {
  const [rules, setRules] = useState(null);
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/groups/${groupId}/rules-summary`);
      setRules(r.data);
    } catch { /* non-fatal */ }
  }, [groupId]);

  useEffect(() => { if (groupId) load(); }, [groupId, load]);

  const share = async () => {
    setSending(true);
    try {
      const r = await api.post(`/groups/${groupId}/rules-summary/send`);
      toast.success(`Rules sent to ${r.data.sent} member${r.data.sent === 1 ? '' : 's'} on WhatsApp`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send the rules');
    } finally {
      setSending(false);
    }
  };

  if (!rules) return null;

  return (
    <>
      <Card className="p-4 border rounded-2xl" data-testid="session-rules-card">
        <div className="flex items-start justify-between gap-2">
          <p className="font-display text-xs font-bold uppercase tracking-[0.16em] inline-flex items-center gap-1.5">
            <CalendarClock className="w-3.5 h-3.5 text-primary" /> How we play
          </p>
          {isAdmin && (
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" className="rounded-full h-8 text-xs" onClick={() => setOpen(true)} data-testid="rules-edit-btn">
                <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
              </Button>
              <Button size="sm" variant="ghost" className="rounded-full h-8 text-xs" onClick={share} disabled={sending} data-testid="rules-share-btn">
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1" /> Share</>}
              </Button>
            </div>
          )}
        </div>

        {rules.schedule_lines.length > 0 ? (
          <ul className="mt-2 space-y-1" data-testid="rules-schedule-lines">
            {rules.schedule_lines.map((l) => (
              <li key={l} className="text-sm font-semibold">{l}</li>
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">
            No repeating schedule yet.{isAdmin ? ' Tap Edit to make sessions appear automatically every week.' : ''}
          </p>
        )}

        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="rules-fines">
          {rules.fines.map((f) => (
            <span key={f.label} className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full bg-muted text-xs font-semibold">
              {f.label} <span className="tabular-nums text-muted-foreground">{f.value}</span>
            </span>
          ))}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{rules.rsvp}</p>
      </Card>

      <SchedulesSheet open={open} groupId={groupId} onClose={() => { setOpen(false); load(); }} onBulkAdd={onBulkAdd} />
    </>
  );
}

function SchedulesSheet({ open, groupId, onClose, onBulkAdd }) {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get(`/groups/${groupId}/session-schedules`);
    setRows(r.data);
  }, [groupId]);

  useEffect(() => { if (open) load().catch(() => {}); }, [open, load]);

  const save = async () => {
    if (!draft.days_of_week.length) return toast.error('Pick at least one day');
    setBusy(true);
    try {
      const exact = draft.rsvp_mode === 'exact';
      if (exact && (!draft.rsvp_open_time || !draft.rsvp_close_time)) {
        setBusy(false);
        return toast.error('Pick both an open and a close time');
      }
      const body = { ...draft, session_fee: Number(draft.session_fee) || 0, num_courts: Number(draft.num_courts) || 1,
        repeat_until: draft.repeat_until || null,
        rsvp_open_days_before: exact ? Number(draft.rsvp_open_days_before) || 0 : null,
        rsvp_open_time: exact ? draft.rsvp_open_time : null,
        rsvp_close_days_before: exact ? Number(draft.rsvp_close_days_before) || 0 : null,
        rsvp_close_time: exact ? draft.rsvp_close_time : null,
      };
      delete body.rsvp_mode;
      if (draft.id) await api.put(`/session-schedules/${draft.id}`, body);
      else await api.post(`/groups/${groupId}/session-schedules`, body);
      toast.success(draft.id ? 'Schedule updated — rules sent to the group' : 'Schedule added — sessions will appear automatically');
      setDraft(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/session-schedules/${id}`);
      toast.success('Schedule removed');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not remove');
    }
  };

  const toggleDay = (i) => setDraft((d) => ({
    ...d,
    days_of_week: d.days_of_week.includes(i) ? d.days_of_week.filter((x) => x !== i) : [...d.days_of_week, i].sort(),
  }));

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" className="rounded-t-3xl max-h-[92vh] overflow-y-auto" data-testid="schedules-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-xl">Repeating sessions</SheetTitle>
          <SheetDescription>Add one schedule per slot — each has its own days, time, courts and fee per player.</SheetDescription>
        </SheetHeader>

        <div className="mt-3 pb-8 space-y-3">
          {rows.map((s) => (
            <div key={s.id} className="rounded-2xl border p-3 flex items-start gap-2" data-testid={`schedule-row-${s.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold truncate">{s.name}</p>
                <p className="text-xs text-muted-foreground">{s.summary}</p>
              </div>
              <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={() => setDraft({
                ...makeBlank(), ...s, repeat_until: s.repeat_until || '',
                rsvp_mode: (s.rsvp_open_days_before != null && s.rsvp_open_time) ? 'exact' : 'relative',
                rsvp_open_days_before: s.rsvp_open_days_before ?? 1,
                rsvp_open_time: s.rsvp_open_time || '20:00',
                rsvp_close_days_before: s.rsvp_close_days_before ?? oneHourBefore(s.start_time).days_before,
                rsvp_close_time: s.rsvp_close_time || oneHourBefore(s.start_time).time,
              })} data-testid={`schedule-edit-${s.id}`}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full text-rose-600" onClick={() => remove(s.id)} data-testid={`schedule-delete-${s.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}

          {!draft ? (
            <div className="space-y-2">
              {onBulkAdd && (
                <Button variant="outline" className="rounded-full w-full h-11" data-testid="schedule-bulk-btn"
                  onClick={() => { onClose?.(); onBulkAdd(); }}>
                  <CalendarClock className="w-4 h-4 mr-1.5" /> Pick dates on a calendar instead
                </Button>
              )}
              <Button className="rounded-full w-full h-11" onClick={() => setDraft(makeBlank())} data-testid="schedule-add-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Add a schedule
              </Button>
            </div>
          ) : (
            <div className="rounded-2xl border p-3 space-y-3" data-testid="schedule-form">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide">Days</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {DAYS.map((d, i) => (
                    <button key={d} type="button" onClick={() => toggleDay(i)} data-testid={`schedule-day-${d.toLowerCase()}`}
                      className={`px-3 h-9 rounded-full border-2 text-xs font-bold transition-colors ${
                        draft.days_of_week.includes(i) ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'
                      }`}>
                      {d}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide">Start time</Label>
                  <Input type="time" value={draft.start_time} onChange={(e) => setDraft({ ...draft, start_time: e.target.value })}
                    className="h-10 rounded-xl border-2" data-testid="schedule-time" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide">Courts</Label>
                  <Input type="number" min="1" value={draft.num_courts} onChange={(e) => setDraft({ ...draft, num_courts: e.target.value })}
                    className="h-10 rounded-xl border-2" data-testid="schedule-courts" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide">Fee / player (₹)</Label>
                  <Input type="number" min="0" value={draft.session_fee} onChange={(e) => setDraft({ ...draft, session_fee: e.target.value })}
                    className="h-10 rounded-xl border-2" data-testid="schedule-fee" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide">Repeat until</Label>
                  <Input type="date" value={draft.repeat_until} onChange={(e) => setDraft({ ...draft, repeat_until: e.target.value })}
                    className="h-10 rounded-xl border-2" data-testid="schedule-until" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide">Repeats</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {CADENCES.map((c) => (
                    <button key={c.id} type="button" onClick={() => setDraft({ ...draft, cadence: c.id })} data-testid={`schedule-cadence-${c.id}`}
                      className={`px-3 h-9 rounded-full border-2 text-xs font-bold transition-colors ${
                        draft.cadence === c.id ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'
                      }`}>
                      {c.label}
                    </button>
                  ))}
                </div>
                {draft.cadence === 'monthly' && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="schedule-week-of-month">
                    {WEEKS.map((w) => (
                      <button key={w.id} type="button" onClick={() => setDraft({ ...draft, week_of_month: w.id })}
                        className={`px-3 h-8 rounded-full border-2 text-xs font-bold ${draft.week_of_month === w.id ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'}`}>
                        {w.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="rounded-xl bg-muted/40 p-2.5 space-y-2.5">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide">RSVP window</Label>
                  <div className="flex gap-1">
                    {[{ id: 'exact', label: 'Date & time' }, { id: 'relative', label: 'Hours before' }].map((m) => (
                      <button key={m.id} type="button" onClick={() => setDraft({ ...draft, rsvp_mode: m.id })}
                        data-testid={`schedule-rsvp-mode-${m.id}`}
                        className={`px-2.5 h-7 rounded-full border-2 text-xs font-bold transition-colors ${
                          draft.rsvp_mode === m.id ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'
                        }`}>
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>

                {draft.rsvp_mode === 'exact' ? (
                  <>
                    {[
                      { key: 'open', label: 'Opens', testid: 'open' },
                      { key: 'close', label: 'Closes', testid: 'close' },
                    ].map(({ key, label, testid }) => (
                      <div key={key} className="space-y-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-bold">{label}</span>
                          <Input type="time" value={draft[`rsvp_${key}_time`] || ''}
                            onChange={(e) => setDraft({ ...draft, [`rsvp_${key}_time`]: e.target.value })}
                            className="h-9 w-[118px] shrink-0 rounded-xl border-2" data-testid={`schedule-rsvp-${testid}-time`} />
                        </div>
                        <div className="flex flex-wrap gap-1.5" data-testid={`schedule-rsvp-${testid}-days`}>
                          {DAYS_BEFORE.map((d) => (
                            <button key={d.id} type="button"
                              onClick={() => setDraft({ ...draft, [`rsvp_${key}_days_before`]: d.id })}
                              data-testid={`schedule-rsvp-${testid}-days-${d.id}`}
                              className={`px-2.5 h-8 rounded-full border-2 text-xs font-bold transition-colors ${
                                Number(draft[`rsvp_${key}_days_before`] ?? 0) === d.id
                                  ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'
                              }`}>
                              {d.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                    <p className="text-xs text-muted-foreground">
                      Counted back from each session's start — e.g. "1 day" + 20:00 on a Sunday session opens Saturday 8 PM.
                    </p>
                  </>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wide">Opens (hrs before)</Label>
                      <Input type="number" min="1" value={draft.rsvp_open_hours_before} onChange={(e) => setDraft({ ...draft, rsvp_open_hours_before: e.target.value })}
                        className="h-10 rounded-xl border-2" data-testid="schedule-rsvp-open" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wide">Closes (hrs before)</Label>
                      <Input type="number" min="0" value={draft.rsvp_close_hours_before} onChange={(e) => setDraft({ ...draft, rsvp_close_hours_before: e.target.value })}
                        className="h-10 rounded-xl border-2" data-testid="schedule-rsvp-close" />
                    </div>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="rounded-full flex-1 h-10" onClick={() => setDraft(null)} data-testid="schedule-cancel">Cancel</Button>
                <Button className="rounded-full flex-1 h-10" onClick={save} disabled={busy} data-testid="schedule-save">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save schedule'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
